import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('dialog:close', this.#handleCartDrawerClose);
  }

  disconnectedCallback() {    
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('dialog:close', this.#handleCartDrawerClose);
    super.disconnectedCallback();
  }

  #handleCartDrawerClose = () => {
    if (typeof window.__monetateFirePageEvents === 'function') {
      window.__monetateFirePageEvents();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  showDialog() {
    super.showDialog();
    this.#trackCartDrawerOpen();

    const roktPlaceholder = this.querySelector('#rokt-placeholder');
    const iFrameLoaded = this.querySelector('#rokt-placeholder iframe');
    if(roktPlaceholder && !iFrameLoaded){
      if(dataLayer) window.dataLayer.push({event: 'roktAdZone'})                    
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Ensure backdrop click closes the drawer immediately
    const { dialog } = this.refs;
    if (dialog) {
      // Handle backdrop clicks - when clicking the backdrop, event.target is the dialog element
      const handleBackdropClick = (event) => {
        if (event.target === dialog) {
          this.closeDialog();
        }
      };
      // Add immediately without setTimeout to ensure it works right away
      dialog.addEventListener('click', handleBackdropClick);
    }
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #trackCartDrawerOpen = async () => {
    if (!window.monetateQ) return;
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      const isEmpty = !cart || cart.item_count === 0;
      window.monetateQ.push(['setPageType', isEmpty ? 'EMPTYCART' : 'CART']);
      if (window.__monetateBaseVars) {
        window.monetateQ.push(['setCustomVariables', window.__monetateBaseVars]);
      }
      const idMap = window.__monetateCartIdMap || {};
      const cartRows = Array.isArray(cart.items)
        ? cart.items
            .filter((item) => idMap[item.product_id] != null)
            .map((item) => ({
              productId: idMap[item.product_id],
              quantity: item.quantity,
              unitPrice: item.final_price / 100,
              currency: cart.currency || cart.currency_code || '',
            }))
        : [];
      if (cartRows.length) {
        window.monetateQ.push(['addCartRows', cartRows]);
      }
      window.monetateQ.push(['trackData']);
    } catch (error) {
      // Silent fail; avoid breaking cart drawer behavior.
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
